import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { serviceDetails } from '../../mocks/serviceDetails';
import { pricingCategories } from '../../mocks/pricing';
import Footer from '../home/components/Footer';

export default function ServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  const service = serviceDetails.find(s => s.id === serviceId);
  
  const pricing = pricingCategories.find(cat => 
    cat.category === service?.pricingCategory
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-rose-50">
        <div className="text-center">
          <h1 className="text-2xl font-playfair text-gray-800 mb-4">Behandlung nicht gefunden</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-rose-400 text-white px-6 py-3 rounded-lg hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
          >
            Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button
              onClick={() => navigate('/')}
              className={`flex items-center space-x-2 cursor-pointer transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-rose-400' : 'text-white hover:text-rose-200'
              }`}
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Zurück</span>
            </button>
            
            <div className="cursor-pointer" onClick={() => navigate('/')}>
              <img
                src="https://static.readdy.ai/image/f83fdda0eecfb5cfc6917dd44bd1d231/54e964387333a21262272d84478a5c26.png"
                alt="Love yourself beauty"
                className="h-10 w-auto"
              />
            </div>

            <a
              href="https://buchung.treatwell.de/ort/445747/menue/"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center space-x-2 cursor-pointer transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-rose-400' : 'text-white hover:text-rose-200'
              }`}
            >
              <i className="ri-calendar-check-line text-xl"></i>
              <span className="hidden sm:inline font-medium whitespace-nowrap">Buchen</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={service.title}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div>
            <div className="w-20 h-20 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-6">
              <i className={`${service.icon} text-4xl text-white`}></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-4">
              {service.title}
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              {service.shortDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Description */}
          <div className="mb-16">
            <h2 className="text-2xl font-playfair font-semibold text-gray-800 mb-6">
              Über diese Behandlung
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              {service.description}
            </p>
          </div>

          {/* Benefits & Process Grid */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Benefits */}
            <div className="bg-rose-50 rounded-2xl p-8">
              <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-6 flex items-center">
                <i className="ri-heart-line text-rose-400 mr-3 text-2xl"></i>
                Ihre Vorteile
              </h3>
              <ul className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <i className="ri-check-line text-rose-400 mt-1 mr-3 flex-shrink-0"></i>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Process */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-6 flex items-center">
                <i className="ri-list-ordered text-rose-400 mr-3 text-2xl"></i>
                Behandlungsablauf
              </h3>
              <ol className="space-y-4">
                {service.process.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-7 h-7 flex items-center justify-center bg-rose-400 text-white rounded-full text-sm font-medium mr-3 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-gray-700 pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Pricing Section */}
          {pricing && (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-rose-400 to-rose-300 px-8 py-6">
                <h3 className="text-2xl font-playfair font-semibold text-white flex items-center">
                  <i className="ri-price-tag-3-line mr-3"></i>
                  Preise & Behandlungszeiten
                </h3>
              </div>
              <div className="p-8">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-4 px-4 font-semibold text-gray-800">Behandlung</th>
                        <th className="text-center py-4 px-4 font-semibold text-gray-800">Dauer</th>
                        <th className="text-right py-4 px-4 font-semibold text-gray-800">Preis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricing.items.map((item, index) => (
                        <tr 
                          key={index} 
                          className={`border-b border-gray-100 hover:bg-rose-50/50 transition-colors ${
                            index % 2 === 0 ? 'bg-gray-50/30' : ''
                          }`}
                        >
                          <td className="py-4 px-4 text-gray-700">{item.name}</td>
                          <td className="py-4 px-4 text-center text-gray-500">{item.duration}</td>
                          <td className="py-4 px-4 text-right font-semibold text-rose-500">{item.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 text-center bg-gradient-to-r from-rose-100 to-rose-50 rounded-2xl p-12">
            <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-4">
              Bereit für Ihre Behandlung?
            </h3>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Vereinbaren Sie jetzt Ihren Termin und lassen Sie sich von uns verwöhnen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://buchung.treatwell.de/ort/445747/menue/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-rose-400 text-white px-8 py-4 rounded-lg hover:bg-rose-500 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line mr-2"></i>
                Jetzt auf Treatwell buchen
              </a>
              <a
                href="https://www.instagram.com/love_yourself_beauty_by_meltem/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white text-rose-500 border-2 border-rose-400 px-8 py-4 rounded-lg hover:bg-rose-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-instagram-line mr-2"></i>
                Auf Instagram schreiben
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
